import { date, object, record, string, union, unknown, type infer as Infer } from 'zod';

/**
 * Permissions-management WS channel schema (GAP-FE-003 / F-045).
 *
 * `PermissionWebSocketMessageAdapter` multiplexes several historical backend
 * shapes for the same logical event (nested `data.entityId`/`data.entity_id`,
 * flat `roleId`/`role_id`, etc.) and previously gated the wire boundary with
 * a bare `typeof message.data === 'object'` check before doing its own
 * priority-ordered field extraction. This schema formalises that boundary
 * check as a Zod `safeParse` (mirroring the dashboard's
 * system-health-websocket-schemas.ts pattern) so a malformed frame (e.g. a
 * non-object/array `data` payload) is rejected with a structured, logged
 * reason instead of silently falling through the extraction branches.
 *
 * Intentionally loose: `data` is `z.record(z.unknown())` (any plain object)
 * because the adapter's job — not this schema's — is to reconcile the
 * multiple field-name variants the backend has emitted over time.
 */
export const PermissionWsEnvelopeSchema = object({
  type: string().optional(),
  data: record(string(), unknown()).nullable().optional(),
  timestamp: union([string(), date()]).optional(),
}).passthrough();

export type PermissionWsEnvelopeZ = Infer<typeof PermissionWsEnvelopeSchema>;
